/**
 * The gameplay wave, as something that can be drawn more than one way.
 *
 * Until now the wave a player taps has been a mandala seal and nothing else —
 * not by decision but by construction. `WaveViewBinder` read a simulation wave,
 * packed it into `ChartInstanceData`, and `RippleField`'s chart shader drew a
 * seal. There was no seam between "what the wave is" and "what the wave looks
 * like", so there was nothing to swap.
 *
 * This file is that seam. It is a **read-only projection** of a simulation wave:
 * every field here already exists on the record, converted to the units a
 * renderer wants and nothing more. No field is invented, and there is no path
 * back — a style cannot move a wave, retime it, or change what tapping it does.
 *
 * ## Why that matters more than usual
 *
 * A renderer that could influence timing would make the game unfair in a way
 * that is almost impossible to notice: the score would depend on which visual
 * the player had selected. So the rule is stated here and enforced by the shape
 * of the type — everything is `readonly`, the projection is built fresh each
 * frame, and nothing downstream is handed the `Wave` itself.
 */

export const GAMEPLAY_WAVE_STYLES = [
  'MANDALA',
  'RIPPLE',
  'SMOKE',
  'CODE',
  'MATH',
  'SUMI',
  'WATERCOLOR',
  'INK_PAINT',
] as const;

export type GameplayWaveStyle = (typeof GAMEPLAY_WAVE_STYLES)[number];

export function isGameplayWaveStyle(value: string): value is GameplayWaveStyle {
  return (GAMEPLAY_WAVE_STYLES as readonly string[]).includes(value);
}

/**
 * What the wave is doing, once it has been judged.
 *
 * Deliberately coarser than the simulation's five timing grades. A renderer
 * needs to know whether to resolve cleanly or to break apart; it does not need
 * to know the difference between PERFECT and GREAT, and giving it that
 * distinction would invite visuals that tell the player their grade before the
 * judgement panel does.
 */
export type GameplayWaveOutcome = 'pending' | 'hit' | 'safe' | 'miss';

export interface GameplayWaveState {
  /** Stable across frames, so a renderer can keep per-wave state. */
  readonly id: number;
  /** Index into the chart, and the seed source. */
  readonly eventIndex: number;
  /** Song time the wave became visible. */
  readonly spawnTime: number;
  /** Playfield position of the wave's origin. Never modified by a style. */
  readonly x: number;
  readonly y: number;
  /** Gate direction in radians — where the wave is "facing". */
  readonly direction: number;
  /** Half-width of the gate, radians. */
  readonly gateHalfWidth: number;
  /**
   * Approach, 0 at spawn and 1 at the moment the wave wants to be tapped.
   *
   * Runs past 1 for a wave that has outlived its best moment. This is the value
   * a style should drive its growth from — it is the only one that means the
   * same thing at every wave speed setting.
   */
  readonly progress: number;
  /** Current radius in world units, straight from the simulation. */
  readonly distance: number;
  /** Radius at which the simulation retires the wave. */
  readonly despawnDistance: number;
  /** Chart-authored intensity, 0..1. */
  readonly strength: number;
  /** Seconds since spawn. */
  readonly lifetime: number;
  /** Deterministic per-wave seed, derived from the event index. */
  readonly seed: number;
  readonly outcome: GameplayWaveOutcome;
  /** 0..1 dissolve after judgement; 0 while pending. */
  readonly resolve: number;
  /** 0..1 glitch after a failed tap, decaying. */
  readonly badFlash: number;
  /** True while the wave is still forming at its origin. */
  readonly warning: boolean;
  /** Fade as the wave nears its despawn radius. */
  readonly life: number;
  /** Authored colour, carried through so styles can honour the chart. */
  readonly red: number;
  readonly green: number;
  readonly blue: number;
}

/** A mutable slot, reused frame to frame so a sync allocates nothing. */
export function createGameplayWaveState(): Mutable<GameplayWaveState> {
  return {
    id: -1, eventIndex: -1, spawnTime: 0, x: 0, y: 0, direction: 0, gateHalfWidth: 0,
    progress: 0, distance: 0, despawnDistance: 0, strength: 0, lifetime: 0, seed: 0,
    outcome: 'pending', resolve: 0, badFlash: 0, warning: false, life: 0,
    red: 1, green: 1, blue: 1,
  };
}

export type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * A per-wave seed that depends on the chart position and nothing else.
 *
 * The same integer hash the mandala binder already uses for its ring rotations,
 * kept identical on purpose: a wave's seed should not change because its style
 * changed, or the two would disagree about which wave is which when a mode
 * switches between them.
 */
export function gameplayWaveSeed(eventIndex: number): number {
  let x = (eventIndex + 0x9e3779b9) | 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  x ^= x >>> 16;
  return x >>> 0;
}
